import { Box, Divider, Tooltip } from '@material-ui/core'
import { Info } from '@material-ui/icons'
import React, { ReactElement, useEffect, useState } from 'react'
import {
  Show,
  ShowProps,
  useTranslate,
  TitleProps,
  TabbedShowLayout,
  Tab,
  TextField,
  FunctionField,
  ReferenceField,
  EditButton,
  useShowContext,
  Record,
  TextInput,
  sanitizeFetchType,
  useQuery,
  DateField,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { UserDto } from '../../../core/dto/user.dto'
import { ResourceName } from '../../../core/ResourceName'
import RelationTab from '../../common/RelationTab'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import ChangePasswordButton from './UserShow/UserChangePasswordButton'
import LockUserButton from './UserShow/LockUserButton'
import relationTabFilter from '../../common/RelationTabFilter'
import { CompanyDto } from '../../../core/dto/company.dto'
import UnlockUserButton from './UserShow/UnlockUserButton'

interface UserTitleProps extends TitleProps {
  record?: UserDto
}

const ShowTitle = (data: UserTitleProps) => {
  const { record: user } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.users.titles.show')}: {user?.id}. {user?.userName}
    </span>
  )
}

const UserNotificationTags = ({ ...props }) => {
  const { record: user } = props
  const hasAuthority = useHasAuthority()

  const UserTagsMapper = (records: Record[], source: string) => ({
    [source]: records.map((record: Record) => record.id),
  })

  const tagsFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="description" alwaysOn />],
      resource: ResourceName.USERS,
    })

  return (
    <RelationTab<UserDto>
      resource={ResourceName.NOTIFICATION_TAGS}
      source="notificationTagsIds"
      mode={hasAuthority(Authority.EDIT_USERS) ? 'edit' : 'show'}
      attachMethod="attachNotificationTagsToUser"
      detachMethod="detachNotificationTagsFromUser"
      attachRequestPayload={(r, ids) => ({
        userId: r.id,
        notificationTagsIds: ids,
      })}
      detachRequestPayload={(r, ids) => ({
        userId: r.id,
        notificationTagsIds: ids,
      })}
      refetchListAfterChange={(filters) => filters?.filterUserId === true}
      relationsRequestMethod="getUserNotificationTags"
      relationsRequestPayload={(r) => ({ id: r.id })}
      relationsMapper={UserTagsMapper}
      filters={tagsFilters() as ReactElement[]}
      filterDefaultValues={{
        userId: user?.id,
        filterUserId: false,
      }}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
    >
      <TextField source="id" label="ID" />
      <TextField source="description" />
    </RelationTab>
  )
}

const UserRoles = ({ ...props }) => {
  const { record: user } = props
  const hasAuthority = useHasAuthority()

  const UserRolesMapper = (records: Record[], source: string) => ({
    [source]: records.map((record: Record) => record.id),
  })

  const rolesFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      resource: ResourceName.USERS,
    })

  return (
    <RelationTab<UserDto>
      resource={ResourceName.ROLES}
      source="rolesIds"
      mode={hasAuthority(Authority.EDIT_USERS) ? 'edit' : 'show'}
      attachMethod="attachRoles"
      detachMethod="detachRoles"
      attachRequestPayload={(r, ids) => ({
        userId: r.id,
        rolesIds: ids,
      })}
      detachRequestPayload={(r, ids) => ({
        userId: r.id,
        rolesIds: ids,
      })}
      refetchListAfterChange={(filters) => filters?.filterUserId === true}
      relationsRequestMethod="getRoles"
      relationsRequestPayload={(r) => ({ userId: r.id })}
      relationsMapper={UserRolesMapper}
      filters={rolesFilters() as ReactElement[]}
      filterDefaultValues={{
        userId: user?.id,
        filterUserId: false,
      }}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
    >
      <TextField source="id" label="ID" />
      <TextField source="name" key="name" />
      <FunctionField
        key="description"
        render={(record) =>
          record?.description ? (
            <Tooltip title={record?.description}>
              <Info />
            </Tooltip>
          ) : (
            <div />
          )
        }
      />
    </RelationTab>
  )
}

const GeneralActions = ({ ...props }) => {
  const { isLockedOut } = props
  const { record: user } = useShowContext<UserDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <ChangePasswordButton />
        {isLockedOut ? <UnlockUserButton /> : <LockUserButton />}
        <EditButton
          basePath={`/${ResourceName.USERS}`}
          record={user}
          variant="contained"
          style={{ margin: '2px' }}
          disabled={!hasAuthority(Authority.EDIT_USERS)}
        />
      </Box>
    </>
  )
}

const UserTabs = ({ ...props }) => {
  const { record: user, loading } = useShowContext<UserDto>()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [isLockedOut, setIsLockedOut] = useState(false)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  useEffect(() => {
    if (!loading && serverDate?.serverLocalDateTime && user?.lockoutEnd) {
      setIsLockedOut(
        new Date(user.lockoutEnd) > new Date(serverDate.serverLocalDateTime),
      )
    } else {
      setIsLockedOut(false)
    }
  }, [loading, serverDate, user, setIsLockedOut])

  return (
    <TabbedShowLayout {...props}>
      <Tab label="resources.users.tabs.general">
        <TextField source="id" label="ID" />
        <TextField source="userName" />
        {user?.lockoutEnd &&
          serverDate?.serverLocalDateTime &&
          new Date(user.lockoutEnd) >
            new Date(serverDate.serverLocalDateTime) && (
            <DateField
              source="lockoutEnd"
              label="resources.users.fields.lockoutEnd"
              showTime
            />
          )}
        {user?.email && <TextField source="email" />}
        {user?.phoneNumber && <TextField source="phoneNumber" />}
        {hasAuthority(Authority.VIEW_COMPANIES) && (
          <ReferenceField
            source="companyId"
            reference={ResourceName.COMPANIES}
            link="show"
          >
            <FunctionField<CompanyDto>
              source="name"
              render={(company?: CompanyDto) =>
                `${company?.id}. ${company?.name}`
              }
            />
          </ReferenceField>
        )}
        <FunctionField<UserDto>
          source="requirePasswordUpdate"
          render={(record?: UserDto) => (
            <div>
              {record?.requirePasswordUpdate
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <GeneralActions loading={loading} isLockedOut={isLockedOut} />
      </Tab>
      {hasAuthority(Authority.VIEW_ROLES) && (
        <Tab label="resources.users.tabs.roles" path="roles">
          <UserRoles />
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_NOTIFICATION_TAGS) && (
        <Tab
          path="notification-tags"
          label={translate('resources.users.tabs.notification-tags')}
        >
          <UserNotificationTags />
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const UserShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <UserTabs />
  </Show>
)
